import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Text, useStdout, measureElement, type DOMElement } from 'ink';
import stringWidth from 'string-width';
import { TextBuffer } from '../text_buffer';
import { maskedPlaceholder } from '../string';
import { useUnifiedInput, KeyChecks } from '../input/unified';
import { computeScrollWindow } from '../scroll_window';
import { useTheme } from '../theme';

interface MultilineTextInputProps {
  value: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
  masked?: boolean;
  ignoreKeys?: string[];
  initialTextBuffer?: TextBuffer;
  onHistoryPrevious?: (currentText: string) => void;
  onHistoryNext?: (currentText: string) => void;
  maxVisibleLines?: number;
  title?: string;
  borderColor?: string;
  promptPrefix?: string;
  /** Optional content rendered as a row inside the bottom border of the input box. */
  footer?: React.ReactNode;
}

export const MultilineTextInput: React.FC<MultilineTextInputProps> = ({
  value,
  onChange,
  placeholder = '',
  masked,
  ignoreKeys = [],
  initialTextBuffer = new TextBuffer(value),
  onHistoryPrevious,
  onHistoryNext,
  maxVisibleLines,
  title,
  borderColor: borderColorProp,
  promptPrefix = '> ',
  footer,
}) => {
  const { tokens } = useTheme();
  const borderColor = borderColorProp ?? tokens.text.accent;
  const [{ textBuffer }, updateTextBuffer] = useState({ textBuffer: initialTextBuffer });
  const { stdout } = useStdout();
  const containerRef = useRef<DOMElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);

  useEffect(() => {
    textBuffer.onBufferChange = () => {
      updateTextBuffer({ textBuffer });
    };
  }, [textBuffer]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const { width } = measureElement(containerRef.current);
    setMeasuredWidth((prev) => (prev === width ? prev : width));
  });

  useUnifiedInput((key) => {
    if (KeyChecks.isEnter(key)) {
      return;
    }

    if (ignoreKeys.includes(key.name)) {
      return;
    }

    if (key.name === 'home' || (key.name === 'a' && key.ctrl)) {
      textBuffer.home();
    } else if (key.name === 'end' || (key.name === 'e' && key.ctrl)) {
      textBuffer.end();
    } else if (
      KeyChecks.isShiftEnter(key) ||
      key.name === 'linefeed' ||
      (key.name === 'j' && key.ctrl)
    ) {
      textBuffer.insert('\n');
    } else if (key.name === 'left' && (key.ctrl || key.meta)) {
      textBuffer.wordLeft();
    } else if (key.name === 'right' && (key.ctrl || key.meta)) {
      textBuffer.wordRight();
    } else if (key.name === 'b' && key.meta) {
      textBuffer.wordLeft();
    } else if (key.name === 'f' && key.meta) {
      textBuffer.wordRight();
    } else if (key.name === 'left') {
      textBuffer.left();
    } else if (key.name === 'right') {
      textBuffer.right();
    } else if (key.name === 'backspace' && (key.ctrl || key.meta)) {
      textBuffer.deleteWordToCursor();
    } else if (key.name === 'backspace') {
      textBuffer.delete();
    } else if (key.name === 'delete' && (key.ctrl || key.meta)) {
      textBuffer.deleteWordFromCursor();
    } else if (key.name === 'delete') {
      textBuffer.deleteAfterCursor();
    } else if (key.name === 'd' && key.ctrl) {
      textBuffer.deleteAfterCursor();
    } else if (key.name === 'u' && key.ctrl) {
      textBuffer.deleteLineToCursor();
    } else if (key.name === 'space') {
      textBuffer.insert(' ');
    } else if (key.name === 'up') {
      const isAtFirstLine = textBuffer.cursor.line === 0;
      if (isAtFirstLine) {
        onHistoryPrevious?.(textBuffer.text);
      } else {
        textBuffer.up();
      }
    } else if (key.name === 'down') {
      const isAtLastLine = textBuffer.cursor.line === textBuffer.lines.length - 1;
      if (isAtLastLine) {
        onHistoryNext?.(textBuffer.text);
      } else {
        textBuffer.down();
      }
    } else if (key.name === 'paste' && key.sequence) {
      textBuffer.insert(key.sequence, true);
    } else if (!key.ctrl && !key.meta && key.name.length >= 1) {
      // Skip named keys (e.g. "tab", "escape") since their names are ASCII-only.
      if (key.name.length > 1 && key.name.charCodeAt(0) <= 127) {
        return;
      }

      // Prefer key.sequence over key.name: for Kitty protocol, sequence contains the
      // actual character produced (e.g. '!' for Shift+1), while key.name holds the base
      // key ('1'). For standard terminals, sequence equals the character sent directly.
      // Fall back to uppercasing key.name for Shift+letter when sequence is not set.
      let input = key.name;
      if (key.sequence && key.sequence.length === 1) {
        input = key.sequence;
      } else if (key.shift && key.name >= 'a' && key.name <= 'z') {
        input = key.name.toUpperCase();
      }
      textBuffer.insert(input);
    }

    if (textBuffer.text !== value) {
      onChange(textBuffer.text);
    }
  });

  // lines are derived from the value prop and hence should not be kept in the component's state
  // but rather be computed from the buffer state on render
  textBuffer.setText(value);
  const { lines, cursor } = textBuffer;

  const termRows = stdout?.rows ?? 24;
  const termCols = stdout?.columns ?? 80;
  // The component may be rendered inside a padded container, so it does not
  // necessarily span the full terminal width. Measure the actual laid-out width
  // and use it to size the hand-built top border and the wrap estimate, falling
  // back to the terminal width until the first layout measurement lands.
  const contentWidth = measuredWidth > 0 ? measuredWidth : termCols;
  const maxRowBudget = maxVisibleLines ?? Math.max(3, Math.floor(termRows * 0.4));

  const { visibleItems, windowStart, showScrollUp, showScrollDown } = computeScrollWindow(
    lines,
    cursor.line,
    maxRowBudget,
    (line) =>
      Math.max(1, Math.ceil(((stringWidth(line) || 1) + promptPrefix.length) / contentWidth)),
  );

  const renderInputWithCursor = () => {
    if (lines.length === 1 && lines[0] === '') {
      return (
        <>
          <Text>█ </Text>
          <Text dimColor>{placeholder}</Text>
        </>
      );
    }

    return (
      <Text>
        {visibleItems.map((line, visibleIdx) => {
          const globalIdx = visibleIdx + windowStart;
          return (
            <React.Fragment key={globalIdx}>
              {globalIdx === cursor.line ? (
                <>
                  <Text>{maskedPlaceholder(line.slice(0, cursor.column), masked)}</Text>
                  <Text inverse>
                    {line[cursor.column] ? maskedPlaceholder(line[cursor.column], masked) : ' '}
                  </Text>
                  <Text>{maskedPlaceholder(line.slice(cursor.column + 1), masked)}</Text>
                </>
              ) : (
                <Text>{maskedPlaceholder(line, masked)}</Text>
              )}
              {visibleIdx < visibleItems.length - 1 && '\n'}
            </React.Fragment>
          );
        })}
      </Text>
    );
  };

  const titleLabel = title ? ` ${title} ` : '';
  const rightMargin = title ? 2 : 0;
  // Only used for the title case, where the label must be overlaid on the border
  // and the dashes have to be hand-built. Sized from the measured content width.
  const topBorderDashes = '─'.repeat(
    Math.max(0, contentWidth - stringWidth(titleLabel) - rightMargin),
  );

  return (
    <Box flexDirection="column" width="100%">
      {title ? (
        <>
          {/* Zero-height, full-width sentinel used to measure the available
              content width for the hand-built (title-overlaid) top border. It
              has no content of its own, so it reports the width the component
              was laid out into rather than being inflated by the border below. */}
          <Box ref={containerRef} width="100%" height={0} />
          <Box>
            <Text color={borderColor}>{topBorderDashes}</Text>
            <Text color={borderColor} inverse>
              {titleLabel}
            </Text>
            <Text color={borderColor}>{'─'.repeat(rightMargin)}</Text>
          </Box>
        </>
      ) : (
        // No title: let Ink draw and size the top border, exactly like the
        // bottom border. Ink sizes borders to the laid-out box width, so this
        // is correct regardless of container padding and needs no measurement.
        <Box
          borderBottom={false}
          borderLeft={false}
          borderRight={false}
          borderStyle="single"
          borderColor={borderColor}
        />
      )}
      <Box
        borderLeft={false}
        borderRight={false}
        borderTop={false}
        borderStyle="single"
        borderColor={borderColor}
        flexDirection="column"
      >
        {showScrollUp && <Text dimColor>{'▲'}</Text>}
        <Box>
          <Text>{promptPrefix}</Text>
          {renderInputWithCursor()}
        </Box>
        {showScrollDown && <Text dimColor>{'▼'}</Text>}
        {footer !== undefined && <Box marginTop={1}>{footer}</Box>}
      </Box>
    </Box>
  );
};
